import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'

export default function TermsPage() {
  return (
    <>
      <Helmet>
        <title>Terms of Service | G6 Optics</title>
        <meta name="description" content="Terms of Service for G6 Optics. Please read these terms carefully before using our mini camera enthusiast website." />
        <link rel="canonical" href="https://g6optics.com/terms" />
        <meta name="robots" content="noindex" />
      </Helmet>

      <article className="legal-page">
        <div className="container">
          <header className="legal-header">
            <h1>Terms of Service</h1>
            <p className="legal-subtitle">Please read these terms carefully before using our website</p>
          </header>

          <div className="legal-content">
            <section className="legal-section">
              <h2>Agreement to Terms</h2>
              <p>
                By accessing and using G6 Optics (the "Site"), you agree to be bound by these Terms of 
                Service ("Terms"). If you do not agree to these Terms, please do not use the Site.
              </p>
              <p>
                We reserve the right to modify these Terms at any time. Your continued use of the Site 
                following any changes constitutes acceptance of those changes.
              </p>
            </section>

            <section className="legal-section">
              <h2>Affiliate Disclosure</h2>
              <p>
                G6 Optics is a participant in the Amazon Services LLC Associates Program, an affiliate 
                advertising program designed to provide a means for sites to earn advertising fees by 
                advertising and linking to Amazon.com.
              </p>
              <p>
                <strong>Important:</strong> G6 Optics is an independent website and is NOT affiliated with, 
                endorsed by, or officially connected to the manufacturers of the G6 camera or any other 
                products featured on this Site. All product names, logos, and brands are property of their 
                respective owners.
              </p>
            </section>

            <section className="legal-section">
              <h2>Use of the Site</h2>
              <p>
                You agree to use the Site only for lawful purposes and in accordance with these Terms. 
                You agree not to:
              </p>
              <ul className="legal-list">
                <li>Use the Site in any way that violates any applicable law or regulation</li>
                <li>Attempt to gain unauthorized access to any portion of the Site</li>
                <li>Use any automated system to access the Site in a manner that sends more requests than a human could reasonably produce</li>
                <li>Introduce any viruses, trojans, or other malicious material</li>
                <li>Collect or harvest any information from the Site without permission</li>
                <li>Impersonate or attempt to impersonate G6 Optics or its operators</li>
              </ul>
            </section>

            <section className="legal-section">
              <h2>Intellectual Property</h2>
              <p>
                The content on this Site, including text, graphics, logos, and images created by G6 Optics, 
                is the property of G6 Optics and is protected by copyright and other intellectual property 
                laws. You may not reproduce, distribute, or create derivative works from this content 
                without our express written permission.
              </p>
              <p>
                Product images, brand names, and trademarks displayed on this Site belong to their respective 
                owners. Their use on this Site does not imply any affiliation with or endorsement by those 
                trademark owners.
              </p>
            </section>

            <section className="legal-section">
              <h2>Product Information Disclaimer</h2>
              <p>
                We strive to provide accurate and up-to-date information about the products featured on 
                our Site. However:
              </p>
              <ul className="legal-list">
                <li>Product specifications, prices, and availability may change without notice</li>
                <li>We cannot guarantee the accuracy of third-party product information</li>
                <li>Photos may not exactly represent the actual product</li>
                <li>Our opinions and reviews are subjective and based on personal experience</li>
              </ul>
              <p>
                Always verify product details on the retailer's website (Amazon.com) before making a purchase.
              </p>
            </section>

            <section className="legal-section">
              <h2>External Links</h2>
              <p>
                Our Site contains links to external websites, primarily Amazon.com. These links are provided 
                for your convenience and do not signify our endorsement of those sites (other than the 
                specific products we recommend). We are not responsible for the content, privacy policies, 
                or practices of external websites.
              </p>
              <p>
                When you click on an affiliate link and are redirected to Amazon.com, you are subject to 
                Amazon's terms of service and privacy policy.
              </p>
            </section>

            <section className="legal-section">
              <h2>Disclaimer of Warranties</h2>
              <p>
                THE SITE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS. G6 OPTICS MAKES NO WARRANTIES, 
                EXPRESS OR IMPLIED, REGARDING THE SITE, INCLUDING BUT NOT LIMITED TO:
              </p>
              <ul className="legal-list">
                <li>The accuracy, completeness, or reliability of any content</li>
                <li>The Site being uninterrupted, secure, or error-free</li>
                <li>The results obtained from using the Site</li>
                <li>The quality of any products purchased through affiliate links</li>
              </ul>
            </section>

            <section className="legal-section">
              <h2>Limitation of Liability</h2>
              <p>
                TO THE FULLEST EXTENT PERMITTED BY LAW, G6 OPTICS SHALL NOT BE LIABLE FOR ANY INDIRECT, 
                INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SITE 
                OR ANY PRODUCTS PURCHASED THROUGH AFFILIATE LINKS.
              </p>
              <p>
                This includes, but is not limited to, damages for loss of profits, data, or other intangible 
                losses, even if we have been advised of the possibility of such damages.
              </p>
            </section>

            <section className="legal-section">
              <h2>Indemnification</h2>
              <p>
                You agree to indemnify and hold harmless G6 Optics, its operators, and affiliates from any 
                claims, losses, damages, liabilities, and expenses (including legal fees) arising from your 
                use of the Site or violation of these Terms.
              </p>
            </section>

            <section className="legal-section">
              <h2>Governing Law</h2>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the United 
                States, without regard to its conflict of law provisions. Any disputes arising from these 
                Terms or your use of the Site shall be resolved in the courts of the United States.
              </p>
            </section>

            <section className="legal-section">
              <h2>Severability</h2>
              <p>
                If any provision of these Terms is found to be unenforceable or invalid, that provision 
                shall be limited or eliminated to the minimum extent necessary, and the remaining provisions 
                shall remain in full force and effect.
              </p>
            </section>

            <section className="legal-section">
              <h2>Contact Information</h2>
              <p>
                If you have any questions about these Terms, please visit our <Link to="/about">About page</Link> for 
                contact information.
              </p>
            </section>

            <footer className="legal-footer">
              <p>Last updated: January 2026</p>
            </footer>
          </div>
        </div>
      </article>
    </>
  )
}
